import { boundarySchoolLinksFromFeatureProperties } from "@/shared/utils/boundary-school-links.util";

import { normalizeGeometryGeoJson } from "./normalize-polygon-geometry.utils";

export { normalizeGeometryGeoJson, normalizePolygonGeometry } from "./normalize-polygon-geometry.utils";

export interface BoundaryDraftBaseline {
  geometryGeoJson: string;
  /** Stable key of all linked schools (sorted internal ids), so adding/removing a non-primary school is a change. */
  schoolsKey?: string;
  /** Stable key of map-level Name + Note (`name` + description). */
  mapLevelMetadataKey?: string;
  deleted: boolean;
}

export enum BaselineCompareMode {
  WithSchool = "withSchool",
  GeometryOnly = "geometryOnly",
  /** Geometry + map-level Name/Note (custom-layers preview). */
  WithMapLevelMetadata = "withMapLevelMetadata",
}

export type BoundaryDraftBaselineStore = Map<string, BoundaryDraftBaseline>;

export type UserTouchedStore = Map<string, boolean>;

export interface LiveEditingSnapshot {
  boundaryId: string;
  geometry: GeoJSON.Polygon;
  properties?: Record<string, unknown>;
}

/** Sorted, deduped `schoolInternalId`s joined into one comparable key (reads `schools[]` or legacy fields). */
export function boundarySchoolsKeyFromProperties(
  props: Record<string, unknown> | undefined,
): string {
  return boundarySchoolLinksFromFeatureProperties(props)
    .map((link) => link.schoolInternalId)
    .join("|");
}

/** Comparable Name + Note key for map-level draft boundaries. */
export function boundaryMapLevelMetadataKeyFromProperties(
  props: Record<string, unknown> | undefined,
): string {
  const name = typeof props?.name === "string" ? props.name : "";
  const description = typeof props?.description === "string" ? props.description : "";
  return `${name}\0${description}`;
}

export function baselineFromFeature(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  mode: BaselineCompareMode,
): BoundaryDraftBaseline {
  const props = feature.properties as Record<string, unknown> | undefined;
  const baseline: BoundaryDraftBaseline = {
    geometryGeoJson: normalizeGeometryGeoJson(feature.geometry),
    deleted: Boolean(props?.deleted),
  };

  if (mode === BaselineCompareMode.WithSchool) {
    baseline.schoolsKey = boundarySchoolsKeyFromProperties(props);
  }

  if (mode === BaselineCompareMode.WithMapLevelMetadata) {
    baseline.mapLevelMetadataKey = boundaryMapLevelMetadataKeyFromProperties(props);
  }

  return baseline;
}

export function geometryMatchesBaseline(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  baseline: BoundaryDraftBaseline,
): boolean {
  return normalizeGeometryGeoJson(feature.geometry) === baseline.geometryGeoJson;
}

export function schoolFieldsMatchBaseline(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  baseline: BoundaryDraftBaseline,
  mode: BaselineCompareMode,
): boolean {
  if (mode !== BaselineCompareMode.WithSchool) {
    return true;
  }

  const props = feature.properties as Record<string, unknown> | undefined;
  return boundarySchoolsKeyFromProperties(props) === (baseline.schoolsKey ?? "");
}

export function mapLevelMetadataFieldsMatchBaseline(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  baseline: BoundaryDraftBaseline,
  mode: BaselineCompareMode,
): boolean {
  if (mode !== BaselineCompareMode.WithMapLevelMetadata) {
    return true;
  }

  const props = feature.properties as Record<string, unknown> | undefined;
  return (
    boundaryMapLevelMetadataKeyFromProperties(props) === (baseline.mapLevelMetadataKey ?? "")
  );
}

export function resetUserTouched(store: UserTouchedStore, boundaryId: string): void {
  if (!boundaryId) {
    return;
  }

  store.set(boundaryId, false);
}

export function markUserTouched(store: UserTouchedStore, boundaryId: string): void {
  if (!boundaryId) {
    return;
  }

  store.set(boundaryId, true);
}

export function clearUserTouched(store: UserTouchedStore, boundaryId: string): void {
  store.delete(boundaryId);
}

export function clearAllUserTouched(store: UserTouchedStore): void {
  store.clear();
}

export function isUserTouched(store: UserTouchedStore, boundaryId: string): boolean {
  return store.get(boundaryId) === true;
}

export function isDraftFeatureDirty(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  baseline: BoundaryDraftBaseline | undefined,
  isNewDraftId: (id: string) => boolean,
  mode: BaselineCompareMode,
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string,
  userTouchedStore: UserTouchedStore,
): boolean {
  const id = getBoundaryId(feature);
  if (!id) {
    return false;
  }

  if (isNewDraftId(id)) {
    return true;
  }

  if (feature.properties?.deleted) {
    return true;
  }

  if (!baseline) {
    return true;
  }

  if (Boolean(feature.properties?.deleted) !== baseline.deleted) {
    return true;
  }

  if (!schoolFieldsMatchBaseline(feature, baseline, mode)) {
    return true;
  }

  if (!mapLevelMetadataFieldsMatchBaseline(feature, baseline, mode)) {
    return true;
  }

  if (!isUserTouched(userTouchedStore, id)) {
    return !geometryMatchesBaseline(feature, baseline);
  }

  return !geometryMatchesBaseline(feature, baseline);
}

export function captureBaselineIfMissing(
  store: BoundaryDraftBaselineStore,
  boundaryId: string,
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  isNewDraftId: (id: string) => boolean,
  mode: BaselineCompareMode,
): void {
  if (!boundaryId || isNewDraftId(boundaryId) || store.has(boundaryId)) {
    return;
  }

  store.set(boundaryId, baselineFromFeature(feature, mode));
}

/** Refresh baseline after a selection-only draft sync refine (not a user edit). */
export function replaceBaselineIfNotUserTouched(
  store: BoundaryDraftBaselineStore,
  boundaryId: string,
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  isNewDraftId: (id: string) => boolean,
  mode: BaselineCompareMode,
  userTouchedStore: UserTouchedStore,
): void {
  if (!boundaryId || isNewDraftId(boundaryId)) {
    return;
  }

  if (isUserTouched(userTouchedStore, boundaryId)) {
    return;
  }

  store.set(boundaryId, baselineFromFeature(feature, mode));
}

export function removeBaseline(
  store: BoundaryDraftBaselineStore,
  boundaryId: string,
  userTouchedStore?: UserTouchedStore,
): void {
  store.delete(boundaryId);
  userTouchedStore?.delete(boundaryId);
}

export function clearBaselines(
  store: BoundaryDraftBaselineStore,
  userTouchedStore?: UserTouchedStore,
): void {
  store.clear();
  userTouchedStore?.clear();
}

export function pruneOrphanBaselines(
  store: BoundaryDraftBaselineStore,
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string,
  userTouchedStore?: UserTouchedStore,
): void {
  const activeIds = new Set(features.map(getBoundaryId).filter(Boolean));

  for (const boundaryId of store.keys()) {
    if (!activeIds.has(boundaryId)) {
      store.delete(boundaryId);
      userTouchedStore?.delete(boundaryId);
    }
  }
}

export function resolveFeaturesForDirtyCheck(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
  liveEditing: LiveEditingSnapshot | null,
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string,
): GeoJSON.Feature<GeoJSON.Polygon>[] {
  if (!liveEditing) {
    return features;
  }

  const { boundaryId, geometry, properties } = liveEditing;
  const idx = features.findIndex((feature) => getBoundaryId(feature) === boundaryId);

  if (idx === -1) {
    return [
      ...features,
      {
        type: "Feature",
        id: boundaryId,
        geometry,
        properties: { ...properties, id: boundaryId },
      },
    ];
  }

  const existing = features[idx];
  const next = [...features];
  next[idx] = {
    ...existing,
    geometry,
    properties: {
      ...(existing.properties as Record<string, unknown> | undefined),
      ...properties,
      id: boundaryId,
    },
  };

  return next;
}

export function getDirtyDraftFeatures(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
  store: BoundaryDraftBaselineStore,
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string,
  isNewDraftId: (id: string) => boolean,
  mode: BaselineCompareMode,
  userTouchedStore: UserTouchedStore,
): GeoJSON.Feature<GeoJSON.Polygon>[] {
  return features.filter((feature) =>
    isDraftFeatureDirty(
      feature,
      store.get(getBoundaryId(feature)),
      isNewDraftId,
      mode,
      getBoundaryId,
      userTouchedStore,
    ),
  );
}

export function hasAnyDirtyDraftFeatures(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
  store: BoundaryDraftBaselineStore,
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string,
  isNewDraftId: (id: string) => boolean,
  mode: BaselineCompareMode,
  userTouchedStore: UserTouchedStore,
): boolean {
  return (
    getDirtyDraftFeatures(
      features,
      store,
      getBoundaryId,
      isNewDraftId,
      mode,
      userTouchedStore,
    ).length > 0
  );
}

export function pruneUnchangedBoundaryIds(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
  store: BoundaryDraftBaselineStore,
  getBoundaryId: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => string,
  isNewDraftId: (id: string) => boolean,
  mode: BaselineCompareMode,
  userTouchedStore: UserTouchedStore,
): string[] {
  return features
    .filter((feature) => {
      const boundaryId = getBoundaryId(feature);
      if (!boundaryId) {
        return false;
      }

      const baseline = store.get(boundaryId);
      if (!baseline) {
        return false;
      }

      return !isDraftFeatureDirty(
        feature,
        baseline,
        isNewDraftId,
        mode,
        getBoundaryId,
        userTouchedStore,
      );
    })
    .map((feature) => getBoundaryId(feature))
    .filter(Boolean);
}

export function syncCombineDraftBaselines(
  store: BoundaryDraftBaselineStore,
  idsToDelete: string[],
  isNewDraftId: (id: string) => boolean,
  userTouchedStore?: UserTouchedStore,
): void {
  for (const polygonId of idsToDelete) {
    const id = String(polygonId);
    if (isNewDraftId(id)) {
      removeBaseline(store, id, userTouchedStore);
    } else {
      userTouchedStore?.delete(id);
    }
  }
}
